import { Link } from "@inertiajs/react"
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Bot,
  CalendarDays,
  Check,
  ChevronDown,
  CircleGauge,
  Clock3,
  FileCheck2,
  Filter,
  Hotel,
  MessageCircleMore,
  MoreHorizontal,
  Paperclip,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  UserRound,
  UsersRound,
} from "lucide-react"
import type { CSSProperties, ReactNode } from "react"
import { AnimatedNumber } from "@/components/marketing/animated-number"
import { SectionReveal } from "@/components/marketing/section-reveal"

type FeaturePoint = {
  title: string
  body: string
}

type Feature = {
  eyebrow: string
  title: string
  description: string
  points: readonly FeaturePoint[]
  cta: string
  href: string
  visual: ReactNode
  reverse?: boolean
}

const conversations = [
  {
    customer: "Atlas Components",
    subject: "Paid account has starter access",
    queue: "Billing",
    waiting: "48 min",
    assigned: "Unowned",
    priority: "High",
  },
  {
    customer: "Cobalt Freight",
    subject: "Monthly report did not generate",
    queue: "Reports",
    waiting: "3h 30m",
    assigned: "Unowned",
    priority: "Normal",
  },
  {
    customer: "Harbor Health",
    subject: "Onboarding transition timed out",
    queue: "Workflow",
    waiting: "5h 25m",
    assigned: "Unowned",
    priority: "High",
  },
] as const

function SampleLabel({ dark = false }: { dark?: boolean }) {
  return (
    <span className="product-shot__sample" data-tone={dark ? "dark" : "light"}>
      Sample data
    </span>
  )
}

function ControlTowerVisual() {
  const metrics = [
    ["4", "Open investigations"],
    ["1", "Unowned case"],
    ["1", "Pending approval"],
    ["3", "Complete escalations"],
    ["6/6", "Demo adapters"],
    ["1", "Conflicting finding"],
  ] as const

  return (
    <div
      className="product-shot product-shot--tower"
      role="img"
      aria-label="Fictional case control view with the unowned-case metric selected and three affected cases open below"
    >
      <div aria-hidden="true">
        <header className="product-shot__topbar">
          <div>
            <CircleGauge />
            <span>
              <strong>Case control</strong>
              <small>All active investigations</small>
            </span>
          </div>
          <div className="product-shot__filters">
            <span>
              All case states <ChevronDown />
            </span>
            <span>
              Last 7 days <CalendarDays />
            </span>
            <span className="product-shot__live">
              <i />
              Updated now
            </span>
            <SampleLabel dark />
          </div>
        </header>

        <div className="product-shot__metric-grid">
          {metrics.map(([value, label], index) => (
            <div
              key={label}
              className="product-shot__metric"
              data-selected={index === 1 || undefined}
            >
              <span>{label}</span>
              <strong>{value}</strong>
              <small>
                {index === 1 ? "Open affected work" : "View detail"}
                <ArrowRight />
              </small>
            </div>
          ))}
        </div>

        <div className="product-shot__tower-body">
          <div className="product-shot__volume">
            <header>
              <span>
                <Activity />
                Investigation activity
              </span>
              <small>24 events this week</small>
            </header>
            <div className="product-shot__volume-chart">
              {[46, 68, 54, 82, 64, 91, 72, 87, 76, 96, 79, 89].map((value) => (
                <i
                  key={value}
                  style={
                    {
                      "--chart-value": `${value}%`,
                    } as CSSProperties
                  }
                />
              ))}
            </div>
          </div>
          <div className="product-shot__conversation-list">
            <header>
              <span>
                <Filter />1 unowned case
              </span>
              <small>Highest priority first</small>
            </header>
            {conversations.map((conversation) => (
              <div key={conversation.subject} className="product-shot__conversation-row">
                <span>
                  <strong>{conversation.subject}</strong>
                  <small>{conversation.customer}</small>
                </span>
                <span>
                  <small>Category</small>
                  {conversation.queue}
                </span>
                <span>
                  <small>Waiting</small>
                  {conversation.waiting}
                </span>
                <span>
                  <small>Assigned</small>
                  {conversation.assigned}
                </span>
                <em data-priority={conversation.priority}>{conversation.priority}</em>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function ConversationWorkspaceVisual() {
  return (
    <div
      className="product-shot product-shot--conversation"
      role="img"
      aria-label="Fictional evidence workspace for Atlas Components with case navigation, an internal support report, cited evidence, ownership, and approval state"
    >
      <div aria-hidden="true">
        <header className="product-shot__topbar">
          <div>
            <MessageCircleMore />
            <span>
              <strong>Evidence workspace</strong>
              <small>Billing entitlement case</small>
            </span>
          </div>
          <div className="product-shot__filters">
            <Search />
            <SampleLabel />
          </div>
        </header>
        <div className="conversation-shot__layout">
          <aside className="conversation-shot__queues">
            <span className="conversation-shot__queue-title">Case views</span>
            {[
              ["Assigned to me", "3"],
              ["Unowned", "1"],
              ["Needs approval", "1"],
              ["Recently updated", "4"],
            ].map(([label, count], index) => (
              <span key={label} data-active={index === 2 || undefined}>
                {index === 0 ? (
                  <UserRound />
                ) : index === 1 ? (
                  <UsersRound />
                ) : index === 2 ? (
                  <AlertTriangle />
                ) : (
                  <Clock3 />
                )}
                {label}
                <small>{count}</small>
              </span>
            ))}
            <div className="conversation-shot__selected">
              <i>AC</i>
              <span>
                <strong>Atlas Components</strong>
                <small>Paid account has starter access</small>
              </span>
              <em>48m</em>
            </div>
          </aside>

          <section className="conversation-shot__thread">
            <header>
              <div>
                <i>AC</i>
                <span>
                  <strong>Atlas Components</strong>
                  <small>Slack · High priority</small>
                </span>
              </div>
              <MoreHorizontal />
            </header>
            <div className="conversation-shot__messages">
              <span className="conversation-shot__date">Today, 10:42</span>
              <p data-sender="customer">
                Atlas paid yesterday, but the workspace still shows starter access. What failed?
                <small>16:42</small>
              </p>
              <p data-sender="agent">
                I’m checking the approved billing and account records now.
                <small>10:44 · Jon</small>
              </p>
              <p className="conversation-shot__note">
                <FileCheck2 />
                Confirmed fact: payment succeeded at 09:51 UTC.
              </p>
              <div className="conversation-shot__draft">
                <span>
                  <Sparkles />
                  Evidence-backed finding
                </span>
                <p>
                  Payment completed, but the entitlement sync did not finish. A bounded re-sync is
                  ready for authorised approval.
                </p>
                <small>Sources: billing fixture · account record · sync event</small>
              </div>
            </div>
            <footer>
              <Paperclip />
              <span>Add evidence or prepare the next step</span>
              <span className="conversation-shot__send">
                Add note
                <Send />
              </span>
            </footer>
          </section>

          <aside className="conversation-shot__context">
            <span className="conversation-shot__context-title">Case context</span>
            <div className="conversation-shot__profile">
              <i>AC</i>
              <span>
                <strong>Atlas Components</strong>
                <small>Fictional customer · Slack intake</small>
              </span>
            </div>
            <dl>
              <div>
                <dt>Customer record</dt>
                <dd>cus_NST_2048</dd>
              </div>
              <div>
                <dt>Owner</dt>
                <dd>Jon Bell</dd>
              </div>
              <div>
                <dt>Confidence</dt>
                <dd data-alert="true">High</dd>
              </div>
              <div>
                <dt>Priority</dt>
                <dd>High</dd>
              </div>
            </dl>
            <div className="conversation-shot__event">
              <Hotel />
              <span>
                <strong>Entitlement sync</strong>
                <small>Failed · Action available</small>
              </span>
              <Check />
            </div>
            <div className="conversation-shot__history">
              <strong>Evidence sources</strong>
              <span>Stripe payment fixture · 09:51</span>
              <span>Account read · 09:53</span>
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}

function GovernedAiVisual() {
  const stages = ["Propose", "Preview", "Authorise", "Approve", "Execute", "Verify"]

  return (
    <div
      className="product-shot product-shot--release"
      role="img"
      aria-label="Fictional governed-action interface for an entitlement re-sync, ready for authorised approval"
    >
      <div aria-hidden="true">
        <header className="product-shot__topbar">
          <div>
            <Bot />
            <span>
              <strong>Governed action control</strong>
              <small>Bounded operational changes</small>
            </span>
          </div>
          <SampleLabel dark />
        </header>
        <div className="release-shot__heading">
          <span>
            <small>Action definition</small>
            <strong>Re-sync paid account entitlement</strong>
          </span>
          <span className="release-shot__status">Ready for approval</span>
        </div>
        <div className="release-shot__summary">
          {[
            ["v3", "Definition version"],
            ["Medium", "Risk level"],
            ["1", "Approval required"],
            ["4", "Cited records"],
          ].map(([value, label]) => (
            <span key={label}>
              <small>{label}</small>
              <strong>{value}</strong>
            </span>
          ))}
        </div>
        <div className="release-shot__body">
          <div className="release-shot__evidence">
            <header>
              <span>
                <ShieldCheck />
                Approval evidence
              </span>
              <small>Policy checks complete; execution not started</small>
            </header>
            <dl>
              <div>
                <dt>Reviewer</dt>
                <dd>Operations manager</dd>
              </div>
              <div>
                <dt>Target record</dt>
                <dd>cus_NST_2048</dd>
              </div>
              <div>
                <dt>Expected effect</dt>
                <dd>Paid access restored</dd>
              </div>
            </dl>
            <div className="release-shot__quality">
              <span>
                Evidence coverage
                <strong>100%</strong>
              </span>
              <i style={{ "--quality": "100%" } as CSSProperties} />
              <span>
                Authority checks
                <strong>100%</strong>
              </span>
              <i style={{ "--quality": "100%" } as CSSProperties} />
            </div>
          </div>
          <div className="release-shot__actions">
            <span>Review evidence</span>
            <span>Approve action</span>
            <span>Reject</span>
          </div>
        </div>
        <div className="release-shot__timeline">
          {stages.map((stage, index) => (
            <span
              key={stage}
              data-complete={index < 3 || undefined}
              data-active={index === 3 || undefined}
            >
              <i>{index < 3 ? <Check /> : index + 1}</i>
              <strong>{stage}</strong>
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

function OperationalImprovementVisual() {
  return (
    <div
      className="product-shot product-shot--insights"
      role="img"
      aria-label="Fictional demo analytics showing case outcomes, ownership, and recurring escalation causes"
    >
      <div aria-hidden="true">
        <header className="product-shot__topbar">
          <div>
            <Activity />
            <span>
              <strong>Resolution analytics</strong>
              <small>Fictional demo period</small>
            </span>
          </div>
          <div className="product-shot__filters">
            <span>
              Demo period <ChevronDown />
            </span>
            <SampleLabel />
          </div>
        </header>
        <div className="insights-shot__metrics">
          {[
            ["62", "Requests", "Fictional"],
            ["31", "Explanations", "Fictional"],
            ["18", "Approved actions", "Fictional"],
            ["9", "Dev escalations", "Fictional"],
          ].map(([value, label, delta]) => (
            <span key={label}>
              <small>{label}</small>
              <strong>{value}</strong>
              <em>{delta}</em>
            </span>
          ))}
        </div>
        <div className="insights-shot__grid">
          <div className="insights-shot__trend">
            <header>
              <strong>Weekly case outcomes</strong>
              <small>Requests and resolved cases</small>
            </header>
            <div className="insights-shot__chart">
              <span className="insights-shot__line insights-shot__line--before" />
              <span className="insights-shot__line insights-shot__line--after" />
              <i>Requests</i>
              <i>Resolved</i>
            </div>
          </div>
          <div className="insights-shot__workload">
            <header>
              <strong>Case ownership</strong>
              <small>Fictional open cases</small>
            </header>
            {[
              ["Jon", 78],
              ["Elena", 64],
              ["Priya", 52],
              ["Noah", 41],
            ].map(([name, value]) => (
              <span key={name as string}>
                <small>{name}</small>
                <i
                  style={
                    {
                      "--workload": `${value}%`,
                    } as CSSProperties
                  }
                />
                <strong>{value}</strong>
              </span>
            ))}
          </div>
          <div className="insights-shot__causes">
            <header>
              <strong>Recurring clarification gaps</strong>
              <small>Fictional cases affected</small>
            </header>
            {[
              ["Missing account identifier", "12"],
              ["Missing event timestamp", "9"],
              ["Conflicting source data", "4"],
            ].map(([label, value]) => (
              <span key={label}>
                {label}
                <strong>{value}</strong>
              </span>
            ))}
            <span className="insights-shot__open-action">
              Open affected cases
              <ArrowRight />
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

const features: readonly Feature[] = [
  {
    eyebrow: "Case control",
    title: "See which investigations need attention.",
    description:
      "Track case state, ownership, confidence, approval, and verification from one operating view.",
    points: [
      {
        title: "Find unowned cases",
        body: "Open every report that still needs an accountable investigator.",
      },
      {
        title: "Keep uncertainty visible",
        body: "See what is confirmed, inferred, contradictory, or still missing.",
      },
      {
        title: "Open the exact evidence",
        body: "Every finding links to the approved records and events behind it.",
      },
    ],
    cta: "Explore case control",
    href: "/product#case-workspace",
    visual: <ControlTowerVisual />,
  },
  {
    eyebrow: "Evidence timeline",
    title: "Give support the evidence before they escalate.",
    description:
      "Bring the original report, approved records, events, findings, and next action into one cited timeline.",
    points: [
      {
        title: "One accountable owner",
        body: "Every case shows who is responsible for the next investigation step.",
      },
      {
        title: "Evidence without guessing",
        body: "Support can see approved application data without arbitrary queries or hidden sources.",
      },
      {
        title: "Complete escalations",
        body: "Engineering receives the timeline, reproduction context, uncertainty, and attempted steps together.",
      },
    ],
    cta: "Explore evidence timelines",
    href: "/product#evidence",
    visual: <ConversationWorkspaceVisual />,
    reverse: true,
  },
  {
    eyebrow: "Governed actions",
    title: "Move from recommendation to controlled execution.",
    description:
      "Preview allowlisted mutations, enforce authority, require approval, and verify the result before completion.",
    points: [
      {
        title: "Preview before approval",
        body: "Show the exact proposed mutation, target record, expected effect, and risk.",
      },
      {
        title: "Enforce authority",
        body: "The server checks role, tenant, policy, case state, and approval before execution.",
      },
      {
        title: "Verify the outcome",
        body: "A re-read confirms the result; partial failure and rollback metadata remain explicit.",
      },
    ],
    cta: "Explore governed actions",
    href: "/product#actions",
    visual: <GovernedAiVisual />,
  },
  {
    eyebrow: "Reusable learning",
    title: "Turn confirmed resolutions into versioned playbooks.",
    description:
      "Reuse validated investigation steps without turning past confidence into future overreach.",
    points: [
      {
        title: "Confirm before reuse",
        body: "A developer validates the cause, evidence, and safe next step.",
      },
      {
        title: "Version every change",
        body: "Playbook edits preserve authorship, review state, scope, and history.",
      },
      {
        title: "Escalate when uncertain",
        body: "When evidence conflicts or scope changes, the playbook stops and hands off.",
      },
    ],
    cta: "Explore versioned playbooks",
    href: "/product#playbooks",
    visual: <OperationalImprovementVisual />,
    reverse: true,
  },
] as const

export function ProductFeatureShowcase() {
  return (
    <section className="workspace-showcase">
      <SectionReveal className="marketing-shell">
        <div className="workspace-showcase__frame" data-assistant-tone="dark">
          <header className="workspace-showcase__intro">
            <span>The Resolto workspace</span>
            <h2>Control every support case from intake to verified resolution.</h2>
            <p>
              Give employees a simple path while developers retain control of access, actions, code
              review, and deployment.
            </p>
          </header>

          <div className="workspace-showcase__indicators">
            <div className="workspace-showcase__indicator-label">
              <span>Example workspace</span>
              <small>Sample data</small>
            </div>
            <section
              className="workspace-showcase__indicator-grid"
              aria-label="Example workspace indicators"
              // biome-ignore lint/a11y/noNoninteractiveTabindex: this responsive horizontal region needs keyboard access.
              tabIndex={0}
            >
              <article>
                <strong>
                  <AnimatedNumber value={4} />
                </strong>
                <span>Open investigations</span>
                <p>Fictional cases awaiting the next evidence step.</p>
              </article>
              <article>
                <strong>
                  <AnimatedNumber value={1} />
                </strong>
                <span>Pending approvals</span>
                <p>Bounded actions awaiting authorised review.</p>
              </article>
              <article>
                <strong>
                  <AnimatedNumber value={3} />
                </strong>
                <span>Complete escalations</span>
                <p>Developer handoffs with cited evidence and context.</p>
              </article>
            </section>
          </div>

          <div className="workspace-showcase__features">
            {features.map((feature) => (
              <article
                key={feature.eyebrow}
                className="workspace-feature"
                data-reverse={feature.reverse || undefined}
              >
                <header className="workspace-feature__intro">
                  <span>{feature.eyebrow}</span>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </header>
                <div className="workspace-feature__visual">{feature.visual}</div>
                <div className="workspace-feature__details">
                  <dl>
                    {feature.points.map((point) => (
                      <div key={point.title}>
                        <dt>{point.title}</dt>
                        <dd>{point.body}</dd>
                      </div>
                    ))}
                  </dl>
                  <Link href={feature.href}>
                    {feature.cta}
                    <ArrowRight aria-hidden="true" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </SectionReveal>
    </section>
  )
}
